(function () {
  if (customElements.get('brand-product-grid')) return;

  class BrandProductGrid extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.dataset.initialized = 'true';
      this.currentPage = 1;
      this.grid = this.querySelector('[data-brand-grid]');
      this.skeleton = this.querySelector('[data-brand-skeleton]');
      this.emptyState = this.querySelector('[data-brand-empty]');
      this.errorState = this.querySelector('[data-brand-error]');
      this.count = this.querySelector('[data-brand-count]');
      this.loadMoreButton = this.querySelector('[data-brand-load-more]');
      this.allProductsLink = this.querySelector('[data-brand-all-products]');
      this.soldOutToggle = this.querySelector('[data-brand-sold-out-toggle]');
      this.sizeFilter = this.querySelector('[data-brand-size-filter]');
      this.sizeOptions = this.querySelector('[data-brand-size-options]');
      this.sizeCount = this.querySelector('[data-brand-size-count]');
      this.sizeClear = this.querySelector('[data-brand-size-clear]');
      this.activeFilters = this.querySelector('[data-brand-active-filters]');
      this.backButton = this.closest('.brand-page')?.querySelector('[data-brand-back]');
      this.selectedSizeIds = new Set();
      this.restoreStateFromUrl();
      this.prepareBackButton();
      this.brandProductsPromise = this.fetchBrandProducts().catch((error) => {
        console.error(error);
        return null;
      });

      this.loadMoreButton?.addEventListener('click', () => {
        if (!this.loadMoreButton.disabled) this.loadProducts(this.currentPage + 1, true);
      });
      this.soldOutToggle?.addEventListener('click', () => {
        if (this.soldOutToggle.disabled) return;
        this.showSoldOut = !this.showSoldOut;
        this.soldOutToggle.setAttribute('aria-pressed', String(this.showSoldOut));
        this.updateUrlState();
        this.loadProducts(1, false);
      });
      this.sizeOptions?.addEventListener('change', (event) => {
        if (event.target.matches('input[type="checkbox"]')) this.updateSizeSelection();
      });
      this.sizeClear?.addEventListener('click', () => this.clearSizeSelection());
      this.activeFilters?.addEventListener('click', (event) => {
        const badge = event.target.closest('[data-brand-size-badge]');
        if (!badge) return;
        const checkbox = Array.from(this.sizeOptions.querySelectorAll('input[type="checkbox"]'))
          .find((input) => input.value === badge.dataset.brandSizeBadge);
        if (checkbox) checkbox.checked = false;
        this.updateSizeSelection();
      });
      this.addSizeFilterDismissListeners();

      this.loadProducts(1, false);
    }

    disconnectedCallback() {
      this.productRequest?.abort();
      this.removeSizeFilterDismissListeners();
      this.backButton?.removeEventListener('click', this.handleBackButtonClick);
    }

    prepareBackButton() {
      if (!this.backButton) return;

      const fallbackUrl = new URL(this.backButton.href, window.location.origin);
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.getAll('size').forEach((value) => {
        if (/^\d+$/u.test(value)) fallbackUrl.searchParams.append('size', value);
      });
      this.backButton.href = fallbackUrl;

      this.handleBackButtonClick ||= (event) => {
        if (!document.referrer) return;
        const referrer = new URL(document.referrer);
        if (referrer.origin !== window.location.origin || window.history.length <= 1) return;
        event.preventDefault();
        window.history.back();
      };
      this.backButton.addEventListener('click', this.handleBackButtonClick);
    }

    sizeUrlValue(id) {
      return String(id).split('/').pop();
    }

    restoreStateFromUrl() {
      const params = new URL(window.location.href).searchParams;
      this.selectedSizeIds = new Set(params.getAll('size')
        .filter((value) => /^\d+$/u.test(value))
        .map((value) => `gid://shopify/Metaobject/${value}`));
      this.showSoldOut = params.get('sold_out') === 'show';
      this.soldOutToggle?.setAttribute('aria-pressed', String(this.showSoldOut));
    }

    updateUrlState() {
      const url = new URL(window.location.href);
      url.searchParams.delete('size');
      Array.from(this.selectedSizeIds)
        .map((id) => this.sizeUrlValue(id))
        .sort((left, right) => left.localeCompare(right, undefined, { numeric: true }))
        .forEach((value) => url.searchParams.append('size', value));
      if (this.showSoldOut) url.searchParams.set('sold_out', 'show');
      else url.searchParams.delete('sold_out');
      window.history.replaceState(window.history.state, '', url);
    }

    addSizeFilterDismissListeners() {
      if (!this.sizeFilter || this.sizeFilterDismissListenersAdded) return;
      this.handleSizeFilterOutsideClick ||= (event) => {
        if (this.sizeFilter.open && !this.sizeFilter.contains(event.target)) {
          this.sizeFilter.removeAttribute('open');
        }
      };
      this.handleSizeFilterEscape ||= (event) => {
        if (event.key !== 'Escape' || !this.sizeFilter.open) return;
        this.sizeFilter.removeAttribute('open');
        this.sizeFilter.querySelector('summary')?.focus();
      };
      document.addEventListener('click', this.handleSizeFilterOutsideClick);
      document.addEventListener('keydown', this.handleSizeFilterEscape);
      this.sizeFilterDismissListenersAdded = true;
    }

    removeSizeFilterDismissListeners() {
      if (!this.sizeFilterDismissListenersAdded) return;
      document.removeEventListener('click', this.handleSizeFilterOutsideClick);
      document.removeEventListener('keydown', this.handleSizeFilterEscape);
      this.sizeFilterDismissListenersAdded = false;
    }

    collectionUrl(page) {
      const url = new URL(this.dataset.collectionUrl, window.location.origin);
      url.searchParams.set(this.dataset.filterParam, this.dataset.brandFilter);
      if (!this.showSoldOut) url.searchParams.set(this.dataset.availabilityParam, '1');
      else url.searchParams.set('fwd_inventory', 'all');
      Array.from(this.selectedSizeIds).forEach((id) => {
        url.searchParams.append(this.dataset.sizeFilterParam, id);
      });

      if (page > 1) url.searchParams.set('page', String(page));

      return url;
    }

    brandProductsUrl(page) {
      const url = new URL(this.dataset.collectionUrl, window.location.origin);
      url.searchParams.set(this.dataset.filterParam, this.dataset.brandFilter);
      url.searchParams.set('view', 'brand-counts');
      if (page > 1) url.searchParams.set('page', String(page));
      return url;
    }

    async fetchBrandProductPage(page) {
      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          const response = await fetch(this.brandProductsUrl(page), {
            credentials: 'same-origin',
            cache: 'no-store',
          });
          if (!response.ok) throw new Error(`Brand product data failed: ${response.status}`);
          const payload = await response.json();
          if (!Array.isArray(payload?.products)) throw new Error('Brand product data was incomplete');
          return payload;
        } catch (error) {
          if (attempt === 2) throw error;
          await new Promise((resolve) => window.setTimeout(resolve, 400 * (2 ** attempt)));
        }
      }
      return { page, pages: page, products: [] };
    }

    async fetchBrandProducts() {
      const firstPage = await this.fetchBrandProductPage(1);
      const remainingPages = Array.from(
        { length: Math.max(0, Number(firstPage.pages) - 1) },
        (_, index) => index + 2
      );
      const remaining = await Promise.all(remainingPages.map((page) => this.fetchBrandProductPage(page)));
      return [firstPage, ...remaining].flatMap((payload) => payload.products);
    }

    aggregateBrandSizeOptions(products) {
      const productCounts = new Map();
      const sizeLabels = new Map();

      products.forEach((product) => {
        const uniqueProductSizes = new Set();
        (product.sizes || []).forEach((size) => {
          if (!size?.id || !size?.label || uniqueProductSizes.has(size.id)) return;
          uniqueProductSizes.add(size.id);
          sizeLabels.set(size.id, size.label);
          if (!this.showSoldOut && !product.available) return;
          productCounts.set(size.id, (productCounts.get(size.id) || 0) + 1);
        });
      });

      return { productCounts, sizeLabels };
    }

    buildSizeFilter(products, documentFragment) {
      if (!this.sizeFilter || !this.sizeOptions || !this.sizeCount || !this.sizeClear) return;
      let productCounts = new Map();
      let sizeLabels = new Map();

      if (Array.isArray(products)) {
        ({ productCounts, sizeLabels } = this.aggregateBrandSizeOptions(products));
      } else {
        const selector = `input[name="${this.dataset.sizeFilterParam}"]`;
        Array.from(documentFragment.querySelectorAll(selector)).forEach((input) => {
          const label = Array.from(documentFragment.querySelectorAll('label'))
            .find((candidate) => candidate.htmlFor === input.id);
          const countText = label?.querySelector('.facet-checkbox__text')?.textContent || '';
          const count = Number(countText.match(/\(([\d,]+)\)\s*$/u)?.[1]?.replace(/,/g, '')) || 0;
          const labelText = countText.replace(/\s*\([\d,]+\)\s*$/u, '').trim();
          if (count > 0 && labelText) {
            sizeLabels.set(input.value, labelText);
            productCounts.set(input.value, count);
          }
        });
      }

      const optionIds = new Set([
        ...productCounts.keys(),
        ...Array.from(this.selectedSizeIds).filter((id) => sizeLabels.has(id)),
      ]);
      const options = Array.from(optionIds)
        .map((id) => [id, sizeLabels.get(id)])
        .filter(([, label]) => label)
        .sort((left, right) => left[1].localeCompare(right[1], undefined, {
          numeric: true,
          sensitivity: 'base',
        }));

      const validIds = new Set(options.map(([id]) => id));
      const validSelection = new Set(Array.from(this.selectedSizeIds).filter((id) => validIds.has(id)));
      const selectionChanged = validSelection.size !== this.selectedSizeIds.size;
      this.selectedSizeIds = validSelection;

      this.sizeLabels = new Map(options);
      this.sizeOptions.replaceChildren(...options.map(([id, label]) => {
        const option = document.createElement('label');
        option.className = 'brand-page__size-option';
        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.value = id;
        checkbox.checked = this.selectedSizeIds.has(id);
        const text = document.createElement('span');
        text.textContent = `${label} (${productCounts.get(id) || 0})`;
        option.append(checkbox, text);
        return option;
      }));
      this.sizeFilter.hidden = options.length === 0;
      this.updateSizeControls();
      if (selectionChanged) this.updateUrlState();
    }

    updateSizeControls() {
      this.sizeCount.textContent = String(this.selectedSizeIds.size);
      this.sizeCount.hidden = this.selectedSizeIds.size === 0;
      this.sizeClear.disabled = this.selectedSizeIds.size === 0;
      this.updateActiveFilterBadges();
    }

    updateActiveFilterBadges() {
      if (!this.activeFilters) return;
      const badges = Array.from(this.selectedSizeIds).map((id) => {
        const badge = document.createElement('button');
        badge.className = 'brand-page__filter-badge';
        badge.type = 'button';
        badge.dataset.brandSizeBadge = id;
        const label = this.sizeLabels?.get(id) || this.sizeUrlValue(id);
        badge.textContent = `${label} ×`;
        badge.setAttribute('aria-label', `${label} 필터 제거`);
        return badge;
      });
      this.activeFilters.replaceChildren(...badges);
      this.activeFilters.hidden = badges.length === 0;
    }

    updateSizeSelection() {
      this.selectedSizeIds = new Set(Array.from(
        this.sizeOptions.querySelectorAll('input[type="checkbox"]:checked')
      ).map((checkbox) => checkbox.value));
      this.updateSizeControls();
      this.updateUrlState();
      this.loadProducts(1, false);
    }

    clearSizeSelection() {
      this.sizeOptions.querySelectorAll('input[type="checkbox"]:checked')
        .forEach((checkbox) => { checkbox.checked = false; });
      this.updateSizeSelection();
    }

    async fetchProductsDocument(page, signal) {
      let lastError;

      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          const response = await fetch(this.collectionUrl(page), {
            credentials: 'same-origin',
            cache: 'no-store',
            signal,
            headers: {
              Accept: 'text/html',
              'X-Requested-With': 'XMLHttpRequest',
            },
          });

          if (!response.ok) throw new Error(`Brand products request failed: ${response.status}`);

          const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
          if (!documentFragment.querySelector('#product-grid')) {
            throw new Error('Brand products grid was not found');
          }

          return documentFragment;
        } catch (error) {
          if (signal.aborted) throw error;
          lastError = error;
          if (attempt < 2) {
            await new Promise((resolve) => window.setTimeout(resolve, 600 * (2 ** attempt)));
          }
        }
      }

      throw lastError;
    }

    async loadProducts(page, append) {
      this.productRequest?.abort();
      const request = new AbortController();
      this.productRequest = request;
      this.setBusy(true, append);

      try {
        const brandProductsRequest = page === 1 ? this.brandProductsPromise : null;
        const documentFragment = await this.fetchProductsDocument(page, request.signal);
        if (this.productRequest !== request || request.signal.aborted) return;
        const sourceGrid = documentFragment.querySelector('#product-grid');

        const items = Array.from(sourceGrid.querySelectorAll(':scope > .grid__item'));
        items.forEach((item) => {
          item.querySelectorAll('.scroll-trigger').forEach((element) => {
            element.classList.remove('scroll-trigger', 'animate--slide-in');
            element.removeAttribute('data-cascade');
            element.style.removeProperty('--animation-order');
          });
          item.classList.remove('scroll-trigger', 'animate--slide-in');
          item.removeAttribute('data-cascade');
          item.style.removeProperty('--animation-order');
        });

        if (append) {
          this.grid.append(...items);
        } else {
          this.grid.replaceChildren(...items);
        }

        this.currentPage = page;
        if (this.allProductsLink) this.allProductsLink.href = this.collectionUrl(1);
        this.updateCount(documentFragment, this.grid.children.length);
        this.updatePagination(documentFragment, page);
        this.showResults(this.grid.children.length > 0);
        document.dispatchEvent(new CustomEvent('collection:product-grid:rendered', {
          detail: { containerId: 'BrandProductGrid' },
        }));

        if (brandProductsRequest) {
          brandProductsRequest.then((brandProducts) => {
            if (this.productRequest !== request || request.signal.aborted) return;
            this.buildSizeFilter(
              Array.isArray(brandProducts) ? brandProducts : null,
              documentFragment
            );
          });
        }
      } catch (error) {
        if (this.productRequest !== request || request.signal.aborted) return;
        this.showError();
        console.error(error);
      } finally {
        if (this.productRequest === request) this.setBusy(false, append);
      }
    }

    updateCount(documentFragment, loadedCount) {
      const sourceCount = documentFragment.querySelector(
        '#ProductCount, #ProductCountDesktop, .product-count__text'
      );
      const matchedCount = sourceCount?.textContent.match(/[\d,.]+/u)?.[0];
      const total = matchedCount || String(loadedCount);
      const numericTotal = Number(total.replace(/,/g, ''));
      const countLabel = numericTotal === 1
        ? this.dataset.countSingularLabel
        : this.dataset.countLabel;
      this.count.textContent = `${total} ${countLabel}`;
    }

    updatePagination(documentFragment, page) {
      const hasNextPage = Boolean(
        documentFragment.querySelector('a.pagination__item--prev[aria-label]')
      );
      if (page === 1) this.hasMultiplePages = hasNextPage;
      this.loadMoreButton.hidden = !hasNextPage;
      this.allProductsLink.hidden = !this.hasMultiplePages;
    }

    showResults(hasProducts) {
      this.skeleton.hidden = true;
      this.errorState.hidden = true;
      this.emptyState.hidden = hasProducts;
      this.grid.hidden = !hasProducts;
      this.allProductsLink.hidden = !hasProducts || !this.hasMultiplePages;
    }

    showError() {
      this.skeleton.hidden = true;
      this.grid.hidden = true;
      this.emptyState.hidden = true;
      this.errorState.hidden = false;
      this.loadMoreButton.hidden = true;
      this.allProductsLink.hidden = !this.hasMultiplePages;
      this.count.textContent = this.dataset.errorCountLabel;
    }

    setBusy(isBusy, append) {
      this.setAttribute('aria-busy', String(isBusy));
      if (this.soldOutToggle) this.soldOutToggle.disabled = isBusy;
      this.sizeOptions?.querySelectorAll('input').forEach((input) => { input.disabled = isBusy; });

      if (isBusy && !append) {
        this.skeleton.hidden = false;
        this.grid.hidden = true;
        this.emptyState.hidden = true;
        this.errorState.hidden = true;
      }

      if (append) {
        this.loadMoreButton.disabled = isBusy;
        this.loadMoreButton.textContent = isBusy
          ? this.dataset.loadingLabel
          : this.dataset.loadMoreLabel;
      }
    }
  }

  customElements.define('brand-product-grid', BrandProductGrid);
})();
